// =============================================================================
// Admin Analytics — Activation view
// =============================================================================
// The BCBA invite → login funnel: Invited → Invites Expired → Logged In →
// Not Yet Logged In. Each card drills into the matching provider list; expired
// invites are rendered visually distinct inside the "Not Yet Logged In" list.
// This view is template-agnostic (the hooks pass templateType: "all").
// =============================================================================
import { useState } from "react";

import { Badge, Column } from "@fronterahealth/frontera-ui-components";

import { DrilldownDialog, DrilldownFilterField } from "@pages/AdminAnalytics/components/DrilldownDialog";
import { MetricCard } from "@pages/AdminAnalytics/components/MetricCard";
import { useWidgetTimeOverride } from "@pages/AdminAnalytics/components/WidgetChrome";
import { cellText } from "@pages/AdminAnalytics/components/charts";
import { useActivationProviders, useInviteFunnel } from "@pages/AdminAnalytics/data/hooks";
import {
  ActivationBucket,
  ActivationProviderRow,
  INVITE_STATUS_LABELS,
  InviteFunnel,
} from "@pages/AdminAnalytics/data/types";
import { ADMIN_ANALYTICS_TITLES } from "@pages/AdminAnalytics/helpers";

const StatusBadge: React.FC<{ row: ActivationProviderRow }> = ({ row }) => {
  if (row.status === "expired") return <Badge appearance="error" text="Expired" dot />;
  if (row.status === "accepted") return <Badge appearance="success" text="Accepted" dot />;
  return <Badge appearance="warn" text="Pending" dot />;
};

const PROVIDER_COLUMNS: Column<ActivationProviderRow>[] = [
  { title: "BCBA", dataIndex: "name", key: "name", render: (v) => cellText(v as string) },
  { title: "Email", dataIndex: "email", key: "email", ellipsis: true, render: (v) => cellText(v as string) },
  { title: "Invited", dataIndex: "invitedAt", key: "invitedAt", render: (v) => cellText(v as string) },
  { title: "Status", dataIndex: "status", key: "status", render: (_v, r) => <StatusBadge row={r} /> },
  {
    title: "Last login",
    dataIndex: "lastLoginAt",
    key: "lastLoginAt",
    render: (v) => (typeof v === "string" ? v : "Never"),
  },
];

const PROVIDER_FILTERS: DrilldownFilterField<ActivationProviderRow>[] = [
  { key: "bcba", label: "BCBA", get: (r) => r.name },
  { key: "email", label: "Email", get: (r) => r.email },
  { key: "invited", label: "Invited", get: (r) => r.invitedAt },
  { key: "status", label: "Status", get: (r) => INVITE_STATUS_LABELS[r.status], kind: "select" },
  { key: "lastLogin", label: "Last login", get: (r) => r.lastLoginAt ?? "Never" },
];

const filterByBucket = (rows: ActivationProviderRow[], bucket: ActivationBucket): ActivationProviderRow[] => {
  switch (bucket) {
    case "invited":
      return rows;
    case "expired":
      return rows.filter((r) => r.status === "expired");
    case "loggedIn":
      return rows.filter((r) => r.hasLoggedIn);
    case "notYetLoggedIn":
      return rows.filter((r) => !r.hasLoggedIn);
  }
};

interface ActivationCardProps {
  title: string;
  infoTooltip: string;
  bucket: ActivationBucket;
  select: (funnel: InviteFunnel) => number;
}

const ActivationCard: React.FC<ActivationCardProps> = ({ title, infoTooltip, bucket, select }) => {
  const timeOverride = useWidgetTimeOverride();
  const [open, setOpen] = useState(false);
  const { data: funnel, isFetching } = useInviteFunnel(timeOverride.override);
  const { data: providers, isFetching: providersFetching } = useActivationProviders(timeOverride.override);

  const rows = filterByBucket(providers ?? [], bucket);

  return (
    <>
      <MetricCard
        title={title}
        infoTooltip={infoTooltip}
        value={funnel ? select(funnel) : 0}
        unit="BCBAs"
        loading={isFetching}
        timeOverride={timeOverride}
        onViewData={() => setOpen(true)}
      />
      <DrilldownDialog
        title={title}
        open={open}
        setOpen={setOpen}
        data={rows}
        columns={PROVIDER_COLUMNS}
        filterFields={PROVIDER_FILTERS}
        exportFileName={`activation-${bucket}`}
        loading={providersFetching}
        header={
          bucket === "notYetLoggedIn" ? (
            <p className="text-xs text-text-secondary">
              Includes both pending and <span className="font-medium text-red-600">expired</span> invites. Expired links
              can no longer be used to join.
            </p>
          ) : undefined
        }
      />
    </>
  );
};

export const ActivationView: React.FC = () => (
  <div className="grid grid-cols-[repeat(auto-fit,minmax(240px,1fr))] gap-4">
    <ActivationCard
      title={ADMIN_ANALYTICS_TITLES.INVITED}
      infoTooltip="Every BCBA sent an invitation to join the org — the top of the funnel."
      bucket="invited"
      select={(f) => f.invited}
    />
    <ActivationCard
      title={ADMIN_ANALYTICS_TITLES.INVITES_EXPIRED}
      infoTooltip="Invites past the 30-day window without being accepted. These links can no longer be used."
      bucket="expired"
      select={(f) => f.expired}
    />
    <ActivationCard
      title={ADMIN_ANALYTICS_TITLES.LOGGED_IN}
      infoTooltip="BCBAs who accepted their invite and logged in at least once."
      bucket="loggedIn"
      select={(f) => f.loggedIn}
    />
    <ActivationCard
      title={ADMIN_ANALYTICS_TITLES.NOT_YET_LOGGED_IN}
      infoTooltip="Invited BCBAs who have never logged in — includes pending and expired invites."
      bucket="notYetLoggedIn"
      select={(f) => f.notYetLoggedIn}
    />
  </div>
);
